/**
 * Custom hook for managing service selection state and logic
 */

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import type {
  ServiceSelectionState,
  TreningSluzba,
  DoplnkovaSluzba,
} from "../types";

export function useServiceSelection(
  treningy: TreningSluzba[],
  doplnky: DoplnkovaSluzba[],
  initialSelection?: Partial<ServiceSelectionState>
) {
  const [selection, setSelection] = useState<ServiceSelectionState>({
    pocetOsob: null,
    treningId: null,
    supplementy: [],
    services: [],
    specialne: [],
    masaze: [],
    vlastnaCena: 0,
    vlastnyPopis: "",
  });

  const hasInitialized = useRef(false);

  // Reset selection when initialSelection changes (for editing)
  useEffect(() => {
    // Only run if we have initialSelection and haven't initialized yet
    if (
      initialSelection &&
      Object.keys(initialSelection).length > 0 &&
      !hasInitialized.current
    ) {
      setSelection(prev => ({
        ...prev,
        pocetOsob: initialSelection.pocetOsob || prev.pocetOsob,
        treningId: initialSelection.treningId || prev.treningId,
        supplementy: initialSelection.supplementy || prev.supplementy,
        services: initialSelection.services || prev.services,
        specialne: initialSelection.specialne || prev.specialne,
        masaze: initialSelection.masaze || prev.masaze,
        vlastnaCena:
          initialSelection.vlastnaCena !== undefined
            ? initialSelection.vlastnaCena
            : prev.vlastnaCena,
        vlastnyPopis: initialSelection.vlastnyPopis || prev.vlastnyPopis,
      }));
      hasInitialized.current = true;
    }
  }, [initialSelection]);

  // Group doplnky by category
  const groupedDoplnky = useMemo(() => {
    return doplnky.reduce(
      (acc, sluzba) => {
        if (!acc[sluzba.kategoria]) {
          acc[sluzba.kategoria] = [];
        }
        acc[sluzba.kategoria].push(sluzba);
        return acc;
      },
      {} as Record<string, DoplnkovaSluzba[]>
    );
  }, [doplnky]);

  // Get training options for selected person count
  const getTrainingOptions = useCallback(
    (pocetOsob: string) => {
      return treningy.filter(t => t.pocetOsob === pocetOsob);
    },
    [treningy]
  );

  // Calculate total price
  const calculateTotal = useCallback(() => {
    let total = 0;

    // Add training price
    if (selection.treningId) {
      const trening = treningy.find(t => t.id === selection.treningId);
      if (trening) total += Number(trening.cena);
    }

    // Add all doplnky
    const allDoplnky = [
      ...selection.supplementy,
      ...selection.services,
      ...selection.specialne,
      ...selection.masaze,
    ];

    allDoplnky.forEach(id => {
      const doplnok = doplnky.find(d => d.id === id);
      if (doplnok) total += Number(doplnok.cena);
    });

    // Add custom price (can be negative)
    total += selection.vlastnaCena;

    return total;
  }, [selection, treningy, doplnky]);

  // Reset selection when person count changes
  const setPocetOsob = useCallback((pocetOsob: "1" | "2" | "3-6") => {
    setSelection(prev => ({
      ...prev,
      pocetOsob,
      treningId: null, // Reset training selection when person count changes
    }));
  }, []);

  // Toggle additional service
  const toggleDoplnok = useCallback(
    (
      kategoria: "supplementy" | "services" | "specialne" | "masaze",
      serviceId: number
    ) => {
      setSelection(prev => {
        const current = prev[kategoria];
        const isSelected = current.includes(serviceId);

        return {
          ...prev,
          [kategoria]: isSelected
            ? current.filter(id => id !== serviceId)
            : [...current, serviceId],
        };
      });
    },
    []
  );

  // Set training ID
  const setTreningId = useCallback((treningId: number) => {
    setSelection(prev => ({ ...prev, treningId }));
  }, []);

  // Set custom price
  const setVlastnaCena = useCallback((vlastnaCena: number) => {
    setSelection(prev => ({ ...prev, vlastnaCena }));
  }, []);

  // Set custom description
  const setVlastnyPopis = useCallback((vlastnyPopis: string) => {
    setSelection(prev => ({ ...prev, vlastnyPopis }));
  }, []);

  // Update multiple doplnky at once
  const setMultipleDoplnky = useCallback(
    (
      supplementy: number[],
      services: number[],
      specialne: number[],
      masaze: number[]
    ) => {
      setSelection(prev => ({
        ...prev,
        supplementy,
        services,
        specialne,
        masaze,
      }));
    },
    []
  );

  // Reset all selections
  const resetSelection = useCallback(() => {
    setSelection({
      pocetOsob: null,
      treningId: null,
      supplementy: [],
      services: [],
      specialne: [],
      masaze: [],
      vlastnaCena: 0,
      vlastnyPopis: "",
    });
    hasInitialized.current = false;
  }, []);

  // Reset with initial values
  const resetWithInitial = useCallback(
    (newInitial: Partial<ServiceSelectionState>) => {
      setSelection({
        pocetOsob: newInitial.pocetOsob || null,
        treningId: newInitial.treningId || null,
        supplementy: newInitial.supplementy || [],
        services: newInitial.services || [],
        specialne: newInitial.specialne || [],
        masaze: newInitial.masaze || [],
        vlastnaCena:
          newInitial.vlastnaCena !== undefined ? newInitial.vlastnaCena : 0,
        vlastnyPopis: newInitial.vlastnyPopis || "",
      });
      hasInitialized.current = true;
    },
    []
  );

  return {
    selection,
    setSelection,
    groupedDoplnky,
    getTrainingOptions,
    calculateTotal,
    setPocetOsob,
    setTreningId,
    toggleDoplnok,
    setVlastnaCena,
    setVlastnyPopis,
    setMultipleDoplnky,
    resetSelection,
    resetWithInitial,
  };
}
